import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Alert, Box, FormControlLabel, Switch } from '@mui/material';
import { LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { modifyOverriddenContactTemplate } from 'apollo/templates/client-directory';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ClientInfoByClinic from './client-info-by-clinic/client-info-by-clinic.components';
import OverridenContactForm from './overriden-contact-form/overriden-contact-form.components';
import { OverrideContactFormData } from 'models/forms';

type ConnectedClientContactProps = {
    labClient: LabClient;
    onContactSaved?: () => void;
};
const ConnectedClientContact: FC<ConnectedClientContactProps> = ({
    labClient,
    onContactSaved,
}: ConnectedClientContactProps) => {
    const { t: translate } = useTranslation(['client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isContactOverridden, setIsContactOverridden] = useState<boolean>(false);

    const [modifyOverriddenContact, { loading }] = useMutation(modifyOverriddenContactTemplate, {
        onCompleted: () => {
            if (onContactSaved) {
                onContactSaved();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (labClient?.clientContent?.isContactOverridden) {
            setIsContactOverridden(labClient.clientContent.isContactOverridden);
        }
    }, [labClient]);

    const toggleClinicOverride = (isChecked: boolean) => {
        setIsContactOverridden(isChecked);
        if (labClient?.clientContent?.isContactOverridden && !isChecked) {
            const variables = {
                clientId: labClient.customerId,
                isContactOverridden: false,
                overriddenContact: labClient?.clientContent?.overriddenContact,
            };
            modifyOverriddenContact({ variables });
        }
    };

    const canBeOverridden = () => {
        return labClient?.customer?.orderVersion === 'Simple' && labClient?.customer?.isWhiteLabeled;
    };

    const handleOverridenContactChanged = (formData: OverrideContactFormData) => {
        modifyOverriddenContact({
            variables: {
                clientId: labClient.customerId,
                isContactOverridden: true,
                overriddenContact: {
                    communication: {
                        email: formData.email,
                        phone: formData.phone,
                        site: formData.site,
                    },
                    address: formData.address,
                },
            },
        });
    };

    return (
        <Fragment>
            <Box sx={{ maxWidth: 900 }}>
                {canBeOverridden() ? (
                    <Box mb={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="secondary"
                                    checked={isContactOverridden}
                                    onChange={(_, value) => toggleClinicOverride(value)}
                                />
                            }
                            label={translate('client-directory:CLINIC_OVERRIDE')}
                        />
                    </Box>
                ) : undefined}
                {isContactOverridden ? (
                    <Box mb={4}>
                        <OverridenContactForm
                            labClient={labClient}
                            onContactChanged={handleOverridenContactChanged}
                            loading={loading}
                        />
                    </Box>
                ) : undefined}
                <Box mb={1}>
                    <ClientInfoByClinic labClient={labClient} />
                </Box>
                {!isContactOverridden && canBeOverridden() ? (
                    <Alert severity="info">{translate('client-directory:CONTACT_OVERWRITE_DESC')}</Alert>
                ) : undefined}
            </Box>
        </Fragment>
    );
};

export default ConnectedClientContact;
